import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, push, onValue, update, get } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

const TAGS = ['Punctual', 'Clean car', 'Friendly', 'Good driving', 'Safe driver'];

export default function RatingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [stars, setStars] = useState(5);
  const [selectedTags, setSelectedTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);

  const pendingRide = searchParams.get('rideId') ? {
    rideId: searchParams.get('rideId'),
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    date: searchParams.get('date') || '',
    otherName: searchParams.get('otherName') || '',
    otherPhone: searchParams.get('otherPhone') || '',
  } : null;

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  useEffect(() => {
    if (!user || !user.phone) return;
    const reviewsRef = ref(db, 'ratings/');
    const unsubscribe = onValue(reviewsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(r => r.aboutPhone === user.phone)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setReviews(list);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t2 => t2 !== tag) : [...prev, tag]);
  };

  const submitRating = async () => {
    if (!pendingRide) return;
    setSubmitting(true);
    try {
      const isLowRating = stars <= 2;
      if (isLowRating) {
        await push(ref(db, 'feedback/'), {
          category: 'Low rating follow-up',
          message: 'Passenger rated ' + stars + ' stars for ride ' + pendingRide.from + ' to ' + pendingRide.to,
          fromName: user.name,
          fromPhone: user.phone,
          aboutName: pendingRide.otherName,
          aboutPhone: pendingRide.otherPhone,
          status: 'open',
          createdAt: new Date().toISOString(),
        });
      } else {
        await push(ref(db, 'ratings/'), {
          stars,
          tags: selectedTags,
          fromName: user.name,
          fromPhone: user.phone,
          aboutName: pendingRide.otherName,
          aboutPhone: pendingRide.otherPhone,
          rideDate: pendingRide.date,
          rideRoute: pendingRide.from + ' → ' + pendingRide.to,
          createdAt: Date.now(),
        });
      }

      const usersSnap = await get(ref(db, 'users/'));
      const usersData = usersSnap.val();
      if (usersData) {
        const otherKey = Object.keys(usersData).find(k => usersData[k].phone === pendingRide.otherPhone);
        if (otherKey) {
          const otherUser = usersData[otherKey];
          const oldRating = otherUser.rating || 0;
          const oldTrips = otherUser.trips || 0;
          const newTrips = oldTrips + 1;
          const newRating = ((oldRating * oldTrips) + stars) / newTrips;
          await update(ref(db, 'users/' + otherKey), {
            rating: Math.round(newRating * 10) / 10,
            trips: newTrips,
          });
        }
      }

      setSubmitting(false);
      alert(isLowRating ? "Thanks — we've forwarded this privately to support." : 'Thanks for rating!');
      navigate('/my-bookings');
    } catch (error) {
      setSubmitting(false);
      alert('Error: ' + error.message);
    }
  };

  if (!user) return null;

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length).toFixed(1)
    : '0.0';

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>⭐ {t('ratings')} and Reviews</h2>

        {pendingRide && (
          <div style={styles.rateCard}>
            <h3 style={{ margin: '0 0 4px', textAlign: 'center', color: '#1a1a2e' }}>{t('rateYourRide')}</h3>
            <p style={{ textAlign: 'center', color: '#5a5f7d', fontSize: 13, margin: '0 0 14px' }}>
              {pendingRide.from} → {pendingRide.to} · {pendingRide.date}
            </p>
            <div style={styles.starsRow}>
              {[1, 2, 3, 4, 5].map(n => (
                <span key={n} style={{ ...styles.star, color: n <= stars ? '#c98a1a' : '#e2e5f0' }} onClick={() => setStars(n)}>★</span>
              ))}
            </div>
            <div style={styles.tagsRow}>
              {TAGS.map(tag => (
                <button key={tag} style={selectedTags.includes(tag) ? styles.tagChipActive : styles.tagChip} onClick={() => toggleTag(tag)}>{tag}</button>
              ))}
            </div>
            {stars <= 2 && (
              <p style={styles.lowRatingNote}>Rating below 3 stars? We'll privately connect you with support instead of posting publicly.</p>
            )}
            <button style={styles.submitBtn} onClick={submitRating} disabled={submitting}>
              {submitting ? 'Submitting...' : t('submitRating')}
            </button>
          </div>
        )}

        <div style={styles.summaryCard}>
          <div style={styles.avgNumber}>{avgRating}</div>
          <div style={{ color: '#c98a1a' }}>★★★★★</div>
          <div style={{ color: '#9499b5', fontSize: 12 }}>{reviews.length} reviews</div>
        </div>

        {reviews.map(r => (
          <div key={r.id} style={styles.reviewCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <b style={{ color: '#1a1a2e' }}>{r.fromName}</b>
              <span style={{ color: '#9499b5', fontSize: 11 }}>{r.rideDate}</span>
            </div>
            <div style={{ color: '#c98a1a', fontSize: 13, margin: '4px 0' }}>{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</div>
            {r.tags && r.tags.length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {r.tags.map(tag => (<span key={tag} style={styles.reviewTag}>{tag}</span>))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 520, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', marginBottom: 20 },
  rateCard: { border: '1.5px solid #3a8fd0', borderRadius: 16, padding: 22, marginBottom: 16, boxShadow: '0 8px 24px rgba(58,143,208,0.1)' },
  starsRow: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 14 },
  star: { fontSize: 32, cursor: 'pointer' },
  tagsRow: { display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 14 },
  tagChip: { border: '1px solid #d9edfa', background: '#f7f9fc', borderRadius: 16, padding: '6px 12px', fontSize: 12, color: '#5a5f7d', cursor: 'pointer' },
  tagChipActive: { border: '1px solid #3a8fd0', background: '#e6f1fb', borderRadius: 16, padding: '6px 12px', fontSize: 12, color: '#185fa5', fontWeight: 700, cursor: 'pointer' },
  lowRatingNote: { background: '#fdf3e3', border: '1px solid #f0c674', borderRadius: 10, padding: 10, fontSize: 11, color: '#8a6416', marginBottom: 14 },
  submitBtn: { width: '100%', height: 46, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 15 },
  summaryCard: { border: '1px solid #eef1f8', borderRadius: 14, padding: 20, marginBottom: 14, textAlign: 'center' },
  avgNumber: { fontSize: 36, fontWeight: 800, color: '#1a1a2e' },
  reviewCard: { border: '1px solid #eef1f8', borderRadius: 12, padding: 12, marginBottom: 10 },
  reviewTag: { background: '#eaf5fc', color: '#3a8fd0', fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '3px 8px' },
};